import React, { useEffect, useRef, useState } from "react";

import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { CSVLink } from "react-csv";

import ButtonWithSpinner from "../ButtonWithSpinner";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { exportToExcel } from "../../utils/exportToExcel";

// Import pode levar bem mais que o timeout padrão da api (30s): a importação
// da conexão processa contato a contato, e a planilha valida números em lote
// com throttle anti-ban.
const IMPORT_TIMEOUT_MS = 300000;

// Modelo de planilha: cabeçalhos exatamente como o backend espera
// (ImportContactsFromFileService aceita variações de caixa/acento, mas o
// modelo mostra a forma canônica). Obrigatório: numero OU email.
const TEMPLATE_ROWS = [
  {
    nome: "Maria Silva",
    numero: "5542999998888",
    email: "maria@empresa.com.br",
    telefone: "(42) 3222-1234",
    email_secundario: "maria.pessoal@gmail.com",
    linkedin: "https://linkedin.com/in/maria-silva"
  }
];

const TEMPLATE_FILENAME = "modelo-importacao-contatos";

const ImportContactsModal = ({ open, onClose, whatsapps, onImported }) => {
  const [mode, setMode] = useState("connection");
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [loading, setLoading] = useState(false);
  const [noContactsNotice, setNoContactsNotice] = useState(false);
  // Importação da conexão sem a agenda completa (backend não conseguiu baixar
  // o snapshot): o modal fica aberto com o aviso em vez do toast de sucesso.
  const [partialResult, setPartialResult] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileResult, setFileResult] = useState(null);
  const fileInputRef = useRef(null);

  const connectedWhatsapps = (whatsapps || []).filter(
    w => w.status === "CONNECTED"
  );

  useEffect(() => {
    if (!open) return;
    setMode("connection");
    setNoContactsNotice(false);
    setPartialResult(null);
    setSelectedFile(null);
    setFileResult(null);
    if (connectedWhatsapps.length && !selectedWhatsappId) {
      setSelectedWhatsappId(connectedWhatsapps[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, whatsapps]);

  const handleClose = () => {
    if (loading) return;
    onClose();
    // Já houve importação por arquivo ou parcial da conexão: a lista precisa
    // recarregar mesmo que o usuário feche pelo backdrop/cancelar.
    if (fileResult || partialResult) {
      onImported();
    }
  };

  const handleImportFromConnection = async () => {
    if (!selectedWhatsappId) return;

    setNoContactsNotice(false);
    setPartialResult(null);
    setLoading(true);
    try {
      const { data } = await api.post(
        "/contacts/import",
        { whatsappId: selectedWhatsappId },
        { timeout: IMPORT_TIMEOUT_MS }
      );
      if (data.addressBookRefreshed === false) {
        setPartialResult(data);
        return;
      }
      toast.success(
        i18n.t("contacts.importModal.success", {
          total: data.total,
          imported: data.imported,
          updated: data.updated
        })
      );
      onClose();
      onImported();
    } catch (err) {
      // Não é bem um "erro" do ponto de vista do usuário: só significa que essa
      // conexão ainda não tem contatos disponíveis para puxar (ver tradução).
      // Mostramos como aviso informativo, não como toast de erro.
      if (err.response?.data?.error === "ERR_NO_CONTACTS_SYNCED_YET") {
        setNoContactsNotice(true);
      } else {
        toastError(err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    setSelectedFile(file || null);
    setFileResult(null);
    // Permite escolher o mesmo arquivo de novo após corrigi-lo
    e.target.value = "";
  };

  const handleImportFromFile = async () => {
    if (!selectedFile) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const { data } = await api.request({
        url: "/contacts/upload",
        method: "POST",
        data: formData,
        timeout: IMPORT_TIMEOUT_MS
      });
      setFileResult(data);
      setSelectedFile(null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadXlsxTemplate = () => {
    exportToExcel(TEMPLATE_ROWS, TEMPLATE_FILENAME, "Contatos");
  };

  const renderConnectionOption = () => (
    <>
      {connectedWhatsapps.length ? (
        <>
          <Typography gutterBottom>
            {i18n.t("contacts.importModal.message")}
          </Typography>
          <FormControl fullWidth variant="outlined" style={{ marginTop: 16 }}>
            <InputLabel id="import-whatsapp-label">
              {i18n.t("contacts.importModal.selectLabel")}
            </InputLabel>
            <Select
              labelId="import-whatsapp-label"
              label={i18n.t("contacts.importModal.selectLabel")}
              value={selectedWhatsappId}
              onChange={e => {
                setSelectedWhatsappId(e.target.value);
                setNoContactsNotice(false);
                setPartialResult(null);
              }}
              disabled={loading}
            >
              {connectedWhatsapps.map(w => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loading && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: 16 }}
            >
              {i18n.t("contacts.importModal.importing")}
            </Typography>
          )}
          {noContactsNotice && (
            <Alert severity="info" style={{ marginTop: 16 }}>
              {i18n.t("backendErrors.ERR_NO_CONTACTS_SYNCED_YET")}
            </Alert>
          )}
          {partialResult && (
            <Alert severity="warning" style={{ marginTop: 16 }}>
              {i18n.t("contacts.importModal.partialWarning", {
                total: partialResult.total,
                imported: partialResult.imported,
                updated: partialResult.updated
              })}
            </Alert>
          )}
        </>
      ) : (
        <Typography color="error">
          {i18n.t("contacts.importModal.noConnection")}
        </Typography>
      )}
    </>
  );

  const renderFileOption = () => (
    <>
      <Typography gutterBottom>
        {i18n.t("contacts.importModal.fileMessage")}
      </Typography>
      <Typography variant="body2" style={{ marginTop: 8 }}>
        <Link component="button" type="button" onClick={handleDownloadXlsxTemplate}>
          {i18n.t("contacts.importModal.downloadTemplateXlsx")}
        </Link>
        {" · "}
        <CSVLink
          data={TEMPLATE_ROWS}
          separator=";"
          filename={`${TEMPLATE_FILENAME}.csv`}
        >
          {i18n.t("contacts.importModal.downloadTemplateCsv")}
        </CSVLink>
      </Typography>
      <input
        type="file"
        accept=".csv,.xls,.xlsx"
        style={{ display: "none" }}
        ref={fileInputRef}
        onChange={handleFileChange}
      />
      <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          {i18n.t("contacts.importModal.selectFile")}
        </Button>
        <Typography variant="body2" color="textSecondary">
          {selectedFile
            ? selectedFile.name
            : i18n.t("contacts.importModal.noFileSelected")}
        </Typography>
      </div>
      {loading && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{ marginTop: 16 }}
        >
          {i18n.t("contacts.importModal.uploading")}
        </Typography>
      )}
      {fileResult && (
        <>
          <Alert severity="success" style={{ marginTop: 16 }}>
            {i18n.t("contacts.importModal.fileSuccess", {
              imported: fileResult.imported,
              updated: fileResult.updated,
              skipped: fileResult.skipped,
              errors: fileResult.errors
            })}
          </Alert>
          {fileResult.validationEnabled && fileResult.validated === false && (
            <Alert severity="warning" style={{ marginTop: 8 }}>
              {i18n.t("contacts.importModal.notValidatedWarning")}
            </Alert>
          )}
          {fileResult.invalidCount > 0 && (
            <Alert severity="info" style={{ marginTop: 8 }}>
              {i18n.t("contacts.importModal.invalidNumbersInfo", {
                count: fileResult.invalidCount
              })}
            </Alert>
          )}
        </>
      )}
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{i18n.t("contacts.importModal.title")}</DialogTitle>
      <DialogContent dividers>
        <RadioGroup
          row
          value={mode}
          onChange={e => {
            setMode(e.target.value);
            setNoContactsNotice(false);
            setPartialResult(null);
          }}
        >
          <FormControlLabel
            value="connection"
            control={<Radio color="primary" />}
            label={i18n.t("contacts.importModal.optionConnection")}
            disabled={loading}
          />
          <FormControlLabel
            value="file"
            control={<Radio color="primary" />}
            label={i18n.t("contacts.importModal.optionFile")}
            disabled={loading}
          />
        </RadioGroup>
        <div style={{ marginTop: 8 }}>
          {mode === "connection" ? renderConnectionOption() : renderFileOption()}
        </div>
      </DialogContent>
      <DialogActions>
        {fileResult ? (
          <Button variant="contained" color="primary" onClick={handleClose}>
            {i18n.t("contacts.importModal.closeAfterImport")}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {i18n.t("confirmationModal.buttons.cancel")}
            </Button>
            <ButtonWithSpinner
              variant="contained"
              color="primary"
              onClick={
                mode === "connection"
                  ? handleImportFromConnection
                  : handleImportFromFile
              }
              disabled={
                loading ||
                (mode === "connection" ? !selectedWhatsappId : !selectedFile)
              }
              loading={loading}
            >
              {i18n.t("contacts.buttons.import")}
            </ButtonWithSpinner>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsModal;
